import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/reusable/Table"
import { api } from "~/utils/api"
import { sumBy } from "lodash"
import { groupBy } from "~/utils/tsUtils"

import { Card, CardContent, CardHeader } from "../reusable/Card"
import { formatNumberWithSuffix } from "~/utils/formatting"
import { Alert } from "../reusable/Alert"

const generateLifecycleStatusText = (lifecycleStatus: string) => {
  switch (lifecycleStatus) {
    case "phasingOut":
      return "Phasing out"
    case "phasedOut":
      return "Phased out"
    default:
      return ""
  }
}

export const SuggestedOrdersCard = ({
  suggestedOrders,
  skuIds,
  lifecycleStatus,
  isFashion,
  isInThePast,
  headerRight,
}: {
  suggestedOrders: { warehouseId: string; quantity: number }[]
  skuIds: string[]
  lifecycleStatus: string | undefined
  isFashion?: boolean
  isInThePast?: boolean
  headerRight?: React.ReactNode
}) => {
  const { data: warehouses } = api.warehouse.get.useQuery()

  return (
    <Card className="max-h-[600px] max-w-[400px] overflow-y-auto">
      <CardHeader>
        <span>Suggested Orders</span>
        {headerRight}
      </CardHeader>
      {!suggestedOrders || suggestedOrders?.length === 0 || isFashion ? (
        <CardContent className="flex flex-col gap-2 p-4">
          <p>No suggested orders {skuIds.length > 1 ? "for these skus" : "for this sku"}.</p>
          {isFashion ? (
            <p>
              This is a <b>fashion product</b> and does not receive suggested orders as these
              products typically have different replenishment strategies than regular products.
            </p>
          ) : lifecycleStatus ? (
            <p>
              Is marked as <b>&apos;{generateLifecycleStatusText(lifecycleStatus)}&apos;</b> and
              will thus not get suggested orders.
            </p>
          ) : null}
        </CardContent>
      ) : (
        <CardContent className="p-0">
          {isInThePast && (
            <div className="p-4">
              <Alert variant={"warning"} className="flex flex-col gap-2">
                <h2 className="font-bold">This order should have been placed already</h2>
                <p>
                  To avoid potential stockouts, consider expediting the shipment (e.g. switching
                  from sea to air) if possible
                </p>
              </Alert>
            </div>
          )}
          <Table>
            <TableHeader>
              <TableRow className="text-xs">
                <TableHead>WAREHOUSE</TableHead>
                <TableHead>QUANTITY</TableHead>
                <TableHead>NO. VARIANTS</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className="text-xs">
              {Object.entries(groupBy(suggestedOrders, "warehouseId")).map(
                ([warehouseId, orders]) => {
                  const warehouse = (warehouses ?? []).find((w) => w.id === warehouseId)
                  return (
                    <TableRow key={warehouseId}>
                      <TableCell>
                        <span>{warehouse?.name ?? warehouseId}</span>
                      </TableCell>
                      <TableCell>{formatNumberWithSuffix(sumBy(orders, "quantity"), 1)}</TableCell>
                      <TableCell>{orders.length}</TableCell>
                    </TableRow>
                  )
                },
              )}
            </TableBody>
          </Table>
        </CardContent>
      )}
    </Card>
  )
}
